const DEFAULT_KEY = process.env.REACT_APP_AES_KEY;
const DEFAULT_IV = process.env.REACT_APP_AES_IV;

export const sha256 = async (text) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

    return hashHex;
};

export const sha256Sync = (text) => {
    console.warn('Using async SHA-256. Consider using sha256() for better performance.');
    return sha256(text);
};


export const sha256Batch = async (texts) => {
    const hashes = await Promise.all(texts.map((text) => sha256(text)));
    return hashes;
};

export const verifySha256 = async (text, hash) => {
    const computedHash = await sha256(text);
    return computedHash === hash;
};


export const aesEncrypt = async (plainText, key = DEFAULT_KEY, iv = DEFAULT_IV) => {
    if (!plainText) return '';

    try {
        const key32 = key.padEnd(32, '0').substring(0, 32);
        const iv16 = iv.padEnd(16, '0').substring(0, 16);

        const encoder = new TextEncoder();
        const keyBuffer = encoder.encode(key32);
        const ivBuffer = encoder.encode(iv16);

        const keyImport = await crypto.subtle.importKey(
            'raw',
            keyBuffer,
            'AES-CBC',
            false,
            ['encrypt']
        );

        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-CBC', iv: ivBuffer },
            keyImport,
            encoder.encode(plainText)
        );

        const bytes = new Uint8Array(encrypted);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    } catch (error) {
        console.error('Encryption error:', error);
        throw new Error('Failed to encrypt data');
    }
};

export const aesDecrypt = async (encryptedText, key = DEFAULT_KEY, iv = DEFAULT_IV) => {
    if (!encryptedText) return '';

    try {
        const key32 = key.padEnd(32, '0').substring(0, 32);
        const iv16 = iv.padEnd(16, '0').substring(0, 16);

        const encoder = new TextEncoder();
        const keyBuffer = encoder.encode(key32);
        const ivBuffer = encoder.encode(iv16);

        const binary = atob(encryptedText);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        const keyImport = await crypto.subtle.importKey(
            'raw',
            keyBuffer,
            'AES-CBC',
            false,
            ['decrypt']
        );

        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-CBC', iv: ivBuffer },
            keyImport,
            bytes
        );

        const decoder = new TextDecoder();
        return decoder.decode(decrypted);
    } catch (error) {
        console.error('Decryption error:', error);
        throw new Error('Failed to decrypt data');
    }
};

export const encryptBankingData = async (bankingData) => {
    const { bankHolderName, accountNumber, bankName, routingNumber, description } = bankingData;

    const [encryptedBankHolderName, encryptedAccount, encryptedBankName, encryptedRoutingNumber, encrypteddescription] = await Promise.all([
        aesEncrypt(bankHolderName || ''),
        aesEncrypt(accountNumber || ''),
        aesEncrypt(bankName || ''),
        aesEncrypt(routingNumber || ''),
        aesEncrypt(description || '')
    ]);

    return {
        BankHolderName: bankHolderName || '',
        AccountNumber: accountNumber || '',
        BankName: bankName || '',
        RoutingNumber: routingNumber || '',
        EncryptedBankHolderName: encryptedBankHolderName,
        EncryptedAccountNumber: encryptedAccount,
        EncryptedBankName: encryptedBankName,
        EncryptedRoutingNumber: encryptedRoutingNumber,
        EncryptedDescription: encrypteddescription
    };
};

export const encryptPassword = async (password) => {
    if (!password) return '';
    return await aesEncrypt(password);
};

export const decryptBankingData = async (encryptedBankingData) => {
    const { holderName, accountNumber, bankName, routingNumber } = encryptedBankingData;

    const [decryptedHolderName, decryptedAccount, decryptedBankName, decryptedRouting] = await Promise.all([
        aesDecrypt(holderName || ''),
        aesDecrypt(accountNumber || ''),
        aesDecrypt(bankName || ''),
        aesDecrypt(routingNumber || '')
    ]);

    return {
        holderName: decryptedHolderName,
        accountNumber: decryptedAccount,
        bankName: decryptedBankName,
        routingNumber: decryptedRouting
    };
};

export default sha256;
